import { Outlet, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/hooks/useAuthStore';

export default function AuthLayout() {
  const { isAuthenticated, isLoading, accessToken } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Only redirect if we actually have a token — stale persisted state
  // without a valid token should not redirect away from auth pages
  if (isAuthenticated && accessToken) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-12 flex-col justify-between relative overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>

        {/* Content */}
        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white">HazEasy</h1>
          <p className="mt-2 text-primary-100 text-lg">
            Hazardous Waste Made Easy
          </p>
        </div>

        <div className="space-y-8 relative z-10">
          <div className="space-y-4">
            <h2 className="text-3xl font-semibold text-white leading-tight">
              Self-Service Waste Profiling
            </h2>
            <p className="text-primary-100 text-lg leading-relaxed">
              Streamline your hazardous waste management with our comprehensive
              platform for profiling, classification, and disposal coordination.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-colors">
              <div className="text-2xl font-bold text-white">EPA/RCRA</div>
              <div className="text-primary-100 text-sm mt-1">
                Compliant Classification
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-colors">
              <div className="text-2xl font-bold text-white">DOT</div>
              <div className="text-primary-100 text-sm mt-1">
                Shipping Documentation
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-colors">
              <div className="text-2xl font-bold text-white">TSDF</div>
              <div className="text-primary-100 text-sm mt-1">
                Network Matching
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-colors">
              <div className="text-2xl font-bold text-white">Manifests</div>
              <div className="text-primary-100 text-sm mt-1">
                Digital Generation
              </div>
            </div>
          </div>
        </div>

        <div className="text-primary-200 text-sm relative z-10">
          &copy; {new Date().getFullYear()} HazEasy. All rights reserved.
        </div>
      </div>

      {/* Right side - auth form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-secondary-50">
        <div className="w-full max-w-md">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
